"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import type { ReportSavedConfig } from "@/features/reports/services/report-prefs-api"

const RECENT_CAP = 8
const SAVED_CAP = 100

/**
 * Local cache of report favorites, recently-viewed, and saved configurations. The user chose backend
 * (cross-device) persistence for these, so this store is the offline mirror: `useReportPrefsSync` hydrates it
 * from the server and pushes changes back behind this exact interface.
 */
type ReportPrefsState = {
  favoriteIds: string[]
  recentIds: string[]
  savedConfigs: ReportSavedConfig[]
  isFavorite: (reportId: string) => boolean
  toggleFavorite: (reportId: string) => void
  pushRecent: (reportId: string) => void
  /** Add a saved configuration (replaces one with the same id). */
  saveConfig: (config: ReportSavedConfig) => void
  removeConfig: (id: string) => void
}

export const useReportPrefsStore = create<ReportPrefsState>()(
  persist(
    (set, get) => ({
      favoriteIds: [],
      recentIds: [],
      savedConfigs: [],
      isFavorite: (reportId) => get().favoriteIds.includes(reportId),
      toggleFavorite: (reportId) =>
        set((state) => ({
          favoriteIds: state.favoriteIds.includes(reportId)
            ? state.favoriteIds.filter((id) => id !== reportId)
            : [...state.favoriteIds, reportId],
        })),
      pushRecent: (reportId) =>
        set((state) => ({
          recentIds: [reportId, ...state.recentIds.filter((id) => id !== reportId)].slice(0, RECENT_CAP),
        })),
      saveConfig: (config) =>
        set((state) => ({
          savedConfigs: [config, ...state.savedConfigs.filter((existing) => existing.id !== config.id)].slice(0, SAVED_CAP),
        })),
      removeConfig: (id) =>
        set((state) => ({ savedConfigs: state.savedConfigs.filter((config) => config.id !== id) })),
    }),
    {
      name: "tenant-finance-report-prefs-v1",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        favoriteIds: state.favoriteIds,
        recentIds: state.recentIds,
        savedConfigs: state.savedConfigs,
      }),
    },
  ),
)
